import * as React from 'react';
import { InboxLine, SearchNoneLine, WarningLine } from '@mingcute/react';

import { cn } from '../lib/cn';

export type EmptyStateVariant = 'empty' | 'no-results' | 'error-loading';

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: EmptyStateVariant;
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}

const defaultIcon: Record<EmptyStateVariant, React.ReactNode> = {
  empty: <InboxLine size={40} />,
  'no-results': <SearchNoneLine size={40} />,
  'error-loading': <WarningLine size={40} />,
};

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ variant = 'empty', icon, title, description, action, className, ...props }, ref) => {
    const isError = variant === 'error-loading';
    return (
      <div
        ref={ref}
        role={isError ? 'alert' : 'status'}
        className={cn(
          'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
          className,
        )}
        {...props}
      >
        <div
          className={cn(
            'flex h-16 w-16 items-center justify-center rounded-full',
            isError
              ? 'text-[var(--color-danger)] bg-[var(--color-danger-light)]'
              : 'text-[var(--color-fg-muted)] bg-[var(--color-subtle)]',
          )}
        >
          {icon ?? defaultIcon[variant]}
        </div>
        <h3 className="text-base font-semibold text-[var(--color-fg)]">{title}</h3>
        {description && (
          <p className="max-w-md text-sm text-[var(--color-fg-secondary)]">{description}</p>
        )}
        {action && <div className="mt-2">{action}</div>}
      </div>
    );
  },
);
EmptyState.displayName = 'EmptyState';
